import { BackendApiClient } from '../../auth/api/backend-client';
import { BackendValidationError } from '../../auth/api/investor';
import { requireList, UNREADABLE_RESPONSE_CODE } from '../../../shared/lib/response-shape';
import type { ScopeContext } from '../../auth/model/types';
import type { ConsolidatedNav } from './position';

/**
 * Marks a walk that exhausted its page budget without the cursor ever clearing. Distinct
 * from `UNREADABLE_RESPONSE_CODE`: every page the server sent back was readable, there
 * were just more of them than this budget allows for. A render site that would otherwise
 * show the unreadable-response copy for this failure blames the wrong thing — the history
 * was too long, not malformed.
 */
export const VALUATION_HISTORY_TOO_LONG_CODE = 'valuation-history-too-long';

export function isValuationHistoryTooLongError(error: unknown): boolean {
  return error instanceof BackendValidationError && error.code === VALUATION_HISTORY_TOO_LONG_CODE;
}

const backendClient = new BackendApiClient();

/**
 * One business day of a portfolio's valuation, as `GET /portfolio-valuation` returns it.
 *
 * `consolidated_nav` follows the summary's null contract exactly: a null `nav_amount`
 * beside non-empty `navs` means the rate did not resolve that day, and beside empty `navs`
 * with `position_count > 0` means nothing was priced. `consolidationStateOf` in
 * `./position` reads both fields, so a day and the live summary answer that question the
 * same way.
 */
export interface PortfolioValuation {
  portfolio_id: string;
  reference_time: string;
  navs: Array<{ currency: string; nav_amount: number; nav_scale: number }>;
  consolidated_nav?: ConsolidatedNav | null;
  position_count: number;
  unpriced_position_count: number;
}

interface PortfolioValuationResponse {
  valuations: PortfolioValuation[];
  cursor?: string;
}

export interface PortfolioValuationFilters {
  after?: string;
  currency?: string;
}

const SERVER_PAGE_SIZE = 100;
/**
 * The walk's backstop. One entry per business day is about 252 a year, so this budget
 * holds roughly forty years of daily history — far past anything a portfolio here has —
 * and exists only to bound a server whose cursor never clears, the same reason
 * `getAllPositions` carries one.
 */
const MAX_VALUATION_PAGES = 100;

/**
 * A read the caller abandoned — a navigation away, a period or currency switch, or a
 * newer read of the same portfolio. Distinct from a backend failure so a caller can tell
 * "nobody is waiting for this" from "the history could not be read".
 */
export class PortfolioValuationReadCanceledError extends Error {}

/**
 * A 200 whose rows this chart cannot read is a failure, not an empty history: the render
 * reads `reference_time` and `navs` off every row, and a row missing either would throw
 * during render, outside the `try` around the fetch. Checked here, once, so the component
 * never has to guard against a shape the contract does not allow.
 */
function requireValuations(body: unknown): PortfolioValuation[] {
  const rows = requireList<unknown>(body, 'valuations', 'Unreadable portfolio valuation response');
  return rows.map((row) => {
    const valuation = row as Partial<PortfolioValuation> | null;
    if (
      valuation == null
      || typeof valuation !== 'object'
      || typeof valuation.reference_time !== 'string'
      || !Array.isArray(valuation.navs)
    ) {
      throw new BackendValidationError('Unreadable portfolio valuation row', UNREADABLE_RESPONSE_CODE);
    }
    return valuation as PortfolioValuation;
  });
}

async function getPortfolioValuationPage(
  sessionId: string,
  scopeContext: ScopeContext,
  portfolioId: string,
  filters: PortfolioValuationFilters,
  cursor: string | undefined,
): Promise<PortfolioValuationResponse> {
  const query = new URLSearchParams();
  query.set('portfolio_id', portfolioId);
  if (filters.after) query.set('after', filters.after);
  query.set('limit', String(SERVER_PAGE_SIZE));
  if (cursor) query.set('cursor', cursor);
  const response = await backendClient.get(
    `/portfolio-valuation?${query}`, sessionId, scopeContext,
    filters.currency ? { 'Accept-Currency': filters.currency } : undefined,
  );
  if (response.success && response.data) {
    const body = response.data as { cursor?: string };
    return { valuations: requireValuations(body), cursor: body.cursor };
  }
  if (response.validationError) {
    throw response.validationError;
  }
  throw new BackendValidationError(response.error || 'Failed to fetch the portfolio valuations');
}

/**
 * Every valuation day of a portfolio inside the bounds, ascending, read by following the
 * cursor to its end.
 *
 * Like `getAllPositions`, a page that fails — or a read the caller abandons — aborts the
 * whole thing instead of returning what arrived so far: a series cut short in the middle
 * is a chart that ends on a day the portfolio did not end on, and the reader has no way to
 * tell that from a whole history. A cleared cursor is the only thing that ends the walk
 * claiming a whole series; a page with no rows is not that. A walk that runs out of page
 * budget never got its answer, so it reports `VALUATION_HISTORY_TOO_LONG_CODE` rather than
 * calling what it holds the whole history.
 *
 * Rows are keyed by `reference_time` and a repeat is skipped, mirroring `getAllPositions`:
 * this backend documents one cached whole-series projection with no repeats today, but a
 * page can still carry fewer rows than it holds across a cursor boundary, and a repeated
 * day would otherwise plot twice and inflate the "Valued days" count.
 */
export async function getPortfolioValuations(
  sessionId: string,
  scopeContext: ScopeContext,
  portfolioId: string,
  filters: PortfolioValuationFilters,
  options?: { isCancelled?: () => boolean },
): Promise<PortfolioValuation[]> {
  const valuationByReferenceTime = new Map<string, PortfolioValuation>();
  let cursor: string | undefined;
  for (let page = 0; page < MAX_VALUATION_PAGES; page++) {
    if (options?.isCancelled?.()) {
      throw new PortfolioValuationReadCanceledError('The portfolio valuation read was canceled');
    }
    const response = await getPortfolioValuationPage(sessionId, scopeContext, portfolioId, filters, cursor);
    for (const valuation of response.valuations) {
      if (valuationByReferenceTime.has(valuation.reference_time)) continue;
      valuationByReferenceTime.set(valuation.reference_time, valuation);
    }
    cursor = response.cursor || undefined;
    if (!cursor) return [...valuationByReferenceTime.values()];
  }
  throw new BackendValidationError('The portfolio valuation history did not end', VALUATION_HISTORY_TOO_LONG_CODE);
}
